'use client'

import { useState, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Table, TableHeader, TableRow, TableCell } from '@tiptap/extension-table'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { EditorProvider } from '@/components/ui/editor/context'
import {
  AlignTool,
  ClearFormatTool,
  FontStyleTool,
  HeadingTool,
  HistoryTool,
  ListTool,
  LinkTool,
  Toolbar,
  SourceModeTool,
  PictureTool,
  TableTool,
  BlockquoteTool,
  CodeBlockTool,
  DividerTool
} from '@/components/ui/editor/toolbar'
import { Textarea } from '@/components/ui/textarea'
import { ImageResize } from 'tiptap-extension-resize-image'

type PropsType = {
  placeholder?: string
  autoFocus?: boolean
  value?: string
  onChange?: (html: string) => void
  isError?: boolean
  id?: string
}

const Editor = ({ placeholder, autoFocus, value, onChange, isError = false, id }: PropsType) => {
  const [sourceMode, setSourceMode] = useState(false)
  const [sourceCode, setSourceCode] = useState('')

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        horizontalRule: false
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Type here...'
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          class: 'text-blue-600 underline'
        }
      }),
      Image,
      ImageResize,
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
      HorizontalRule
    ],
    content: value,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'tiptap html-content focus:outline-none min-h-[200px]'
      }
    },
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML())
      }
      setSourceCode(editor.getHTML())
    }
  })

  useEffect(() => {
    if (!editor || value === undefined) {
      return
    }
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
      setSourceCode(value)
    }
  }, [value, editor])

  if (!editor) {
    return null
  }

  return (
    <EditorProvider editor={editor} sourceMode={sourceMode}>
      <div className={`bg-background rounded-md border shadow-sm ${isError ? 'border-destructive' : 'border-border'}`}>
        {id && <span id={id} tabIndex={-1} className="sr-only" onFocus={() => editor.commands.focus()} />}
        <Toolbar>
          <HistoryTool />
          <FontStyleTool />
          <AlignTool />
          <ListTool />
          <HeadingTool />
          <LinkTool />
          <PictureTool />
          <TableTool />
          <BlockquoteTool />
          <CodeBlockTool />
          <DividerTool />
          <ClearFormatTool />
          <SourceModeTool
            sourceMode={sourceMode}
            setSourceMode={setSourceMode}
            sourceCode={sourceCode}
            setSourceCode={setSourceCode}
          />
        </Toolbar>
        {sourceMode ? (
          <Textarea
            value={sourceCode}
            onChange={e => {
              setSourceCode(e.target.value)
              if (onChange) {
                onChange(e.target.value)
              }
            }}
            className="bg-background text-foreground min-h-50 w-full resize-none border-t p-4 font-mono"
          />
        ) : (
          <EditorContent editor={editor} className="tiptap max-w-none p-5" />
        )}
      </div>
    </EditorProvider>
  )
}

export default Editor
